import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LockClosedIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { StaffBooking } from '../../types/staffBookings';
import {
	bookingDateKey,
	endMinutes,
	formatAddress,
	formatClock,
	formatDay,
	formatDurationShort,
	formatMoney,
	isCompletedForStaff,
	startMinutes,
	type EventColor,
} from '../../utils/staffCalendar';

/** Task badge colours shared by the list cards, the detail modal and the calendar popover. */
export const TASK_BADGE_STYLES: Record<string, string> = {
	Liner: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
	Picker: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-200',
	Prepper: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
	Dropper: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
	Feeder: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
	Whole: 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white',
	'Whole W/O Prepping': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
};

export const taskBadgeClass = (taskType: string): string =>
	TASK_BADGE_STYLES[taskType] ||
	'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400';

const POPOVER_WIDTH = 350;
const EDGE = 10;
/** Below this width the popover becomes a bottom sheet (see the className below). */
const SHEET_BREAKPOINT = 768;

export interface BookingPopoverProps {
	booking: StaffBooking;
	/** Where the clicked event sits; the popover opens beside it and stays on screen. */
	anchorRect: DOMRect;
	color: EventColor;
	statusLabel: string;
	canReviewInvoice: boolean;
	onClose: () => void;
	onShowInList: (booking: StaffBooking) => void;
	onOpenDay: (dateKey: string) => void;
	onOpenInvoice: (booking: StaffBooking) => void;
}

const BookingPopover = ({
	booking,
	anchorRect,
	color,
	statusLabel,
	canReviewInvoice,
	onClose,
	onShowInList,
	onOpenDay,
	onOpenInvoice,
}: BookingPopoverProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

	// Keep the popover inside the viewport: to the right of the event, else to its left; never
	// off the bottom. On narrow screens it is a bottom sheet and needs no coordinates.
	useLayoutEffect(() => {
		if (window.innerWidth < SHEET_BREAKPOINT) {
			setPosition(null);
			return;
		}
		const height = ref.current?.offsetHeight ?? 0;
		let left = anchorRect.right + EDGE;
		if (left + POPOVER_WIDTH > window.innerWidth - EDGE) {
			left = Math.max(EDGE, anchorRect.left - POPOVER_WIDTH - EDGE);
		}
		const top = Math.min(
			Math.max(EDGE, anchorRect.top),
			Math.max(EDGE, window.innerHeight - height - EDGE)
		);
		setPosition({ left, top });
	}, [anchorRect, booking.id]);

	// Close on Escape or on a click outside.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		const onDown = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) onClose();
		};
		document.addEventListener('keydown', onKey);
		document.addEventListener('mousedown', onDown);
		return () => {
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('mousedown', onDown);
		};
	}, [onClose]);

	useEffect(() => {
		ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
	}, [booking.id]);

	const start = startMinutes(booking);
	const end = endMinutes(booking);
	const day = bookingDateKey(booking);
	const cancelled = booking.status === 'CANCELLED';
	const completed = isCompletedForStaff(booking);
	const invoiceState = booking.invoiceState ?? 'NONE';
	const invoiceLocked = invoiceState === 'NONE' && !completed;
	const invoiceText = cancelled
		? '—'
		: invoiceLocked
			? 'Unlocks after completion'
			: invoiceState === 'PAID'
				? 'Paid'
				: invoiceState === 'PENDING'
					? 'Sent'
					: 'Ready';
	const serviceLine = [booking.serviceName, booking.size, booking.hairLength]
		.filter(Boolean)
		.join(' • ');
	const address = booking.location ? formatAddress(booking.location) : '';
	const contact = cancelled
		? '—'
		: [booking.customerPhone, booking.customerEmail].filter(Boolean).join(' · ') ||
			'No contact details on file';

	const rows: [string, string][] = [
		['Service', serviceLine || '—'],
		['Salon', booking.providerBusinessName || '—'],
		['Address', address || '—'],
		[
			'Duration',
			`${formatDurationShort(booking.durationMinutes)}${booking.durationEstimated ? ' (est.)' : ''}`,
		],
		[
			'Price',
			`${formatMoney(booking.amount)}${booking.tipAmount ? ` + ${formatMoney(booking.tipAmount)} tip` : ''}`,
		],
		['Contact', contact],
		['Invoice', invoiceText],
	];

	const actionClass =
		'inline-flex items-center justify-center gap-1.5 rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-2 text-sm font-semibold text-[#5a4fcf] dark:text-indigo-300 hover:border-[#667eea] hover:bg-indigo-50 dark:hover:bg-indigo-900/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#667eea] disabled:cursor-not-allowed disabled:text-gray-400 dark:disabled:text-gray-500 disabled:hover:border-gray-200 disabled:hover:bg-transparent';

	return (
		<div
			ref={ref}
			role="dialog"
			aria-modal="false"
			aria-label={`Booking: ${booking.customerName || 'Unknown'}`}
			className="fixed z-[120] overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl inset-x-3 bottom-3 md:inset-auto md:w-[350px]"
			style={position ? { left: position.left, top: position.top } : undefined}
		>
			<div className="relative px-4 py-3.5 text-white" style={{ background: color.border }}>
				<div className="pr-8 text-xs font-semibold opacity-90">
					{formatDay(day, {
						weekday: 'short',
						month: 'short',
						day: 'numeric',
						year: 'numeric',
					})}{' '}
					&middot; {formatClock(start)} &ndash; {formatClock(end)}
					{end > 24 * 60 ? ' (+1)' : ''} &middot; {statusLabel}
				</div>
				<div className="mt-0.5 text-lg font-extrabold">
					{booking.customerName || 'Unknown'}
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="absolute right-3 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/25 hover:bg-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
				>
					<XMarkIcon className="h-4 w-4" aria-hidden="true" />
				</button>
			</div>

			<div className="px-4 pt-2 pb-1 text-sm">
				{rows.map(([label, value]) => (
					<div
						key={label}
						className="flex justify-between gap-4 border-b border-gray-100 dark:border-gray-700 py-1.5 last:border-b-0"
					>
						<span className="shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
						<span className="text-right font-semibold text-gray-900 dark:text-gray-100 break-words min-w-0">
							{value}
						</span>
					</div>
				))}
				{booking.assignedTasks && booking.assignedTasks.length > 0 && (
					<div className="flex flex-wrap gap-1.5 pt-2.5 pb-1">
						{booking.assignedTasks.map(task => (
							<span
								key={task}
								className={`rounded px-2 py-0.5 text-xs font-semibold ${taskBadgeClass(task)}`}
							>
								{task}
							</span>
						))}
					</div>
				)}
			</div>

			<div className="grid grid-cols-2 gap-2 px-4 pt-3 pb-4">
				{address ? (
					<a
						href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
						target="_blank"
						rel="noopener noreferrer"
						className={actionClass}
					>
						Directions
					</a>
				) : (
					<button
						type="button"
						disabled
						className={actionClass}
						title="No salon address on file"
					>
						Directions
					</button>
				)}
				<button
					type="button"
					className={actionClass}
					disabled={cancelled || invoiceLocked || !canReviewInvoice}
					title={
						!canReviewInvoice
							? 'Your role cannot access invoices'
							: invoiceLocked
								? 'Available once the appointment is completed'
								: undefined
					}
					onClick={() => onOpenInvoice(booking)}
				>
					{invoiceLocked && !cancelled && (
						<LockClosedIcon className="h-4 w-4" aria-hidden="true" />
					)}
					{invoiceState === 'PENDING' ? '✓ Invoice Sent' : 'Invoice'}
				</button>
				<button type="button" className={actionClass} onClick={() => onShowInList(booking)}>
					Show in list
				</button>
				<button type="button" className={actionClass} onClick={() => onOpenDay(day)}>
					Open day
				</button>
			</div>
		</div>
	);
};

export default BookingPopover;
